import React, { useCallback, useRef, useState, useMemo } from 'react';
import { Button, makeStyles, Typography } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { EditorTable, TableShape } from '../context';
import { useTableMapWizard } from '../context';
import MapEditorCanvas, {
    ROOM,
    ZOOM_MIN,
    ZOOM_MAX,
    ZOOM_STEP,
} from '../components/MapEditorCanvas';
import TableFigure from '../components/TableFigure';
import SelectionHighlight from '../components/SelectionHighlight';
import SectionTitle from '../components/SectionTitle';

const DRAG_THRESHOLD = 4;
const ERROR_COLOR = theme.palette.error.main;

const MAGENTA = theme.palette.primary.main;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';
const MAGENTA_BORDER = 'rgba(153, 86, 126, 0.3)';

interface TableTypeConfig {
    shape: TableShape;
    seats: number;
    /** normalized width (0..1 of room width) */
    w: number;
    /** normalized height (0..1 of room height) */
    h: number;
    labelKey: string;
}

const TABLE_TYPES: TableTypeConfig[] = [
    { shape: 'round', seats: 4, w: 0.06, h: 0.1, labelKey: 'tableMaps.wizard.roundTable' },
    { shape: 'square', seats: 4, w: 0.06, h: 0.1, labelKey: 'tableMaps.wizard.squareTable' },
    {
        shape: 'rectangle',
        seats: 8,
        w: 0.12,
        h: 0.1,
        labelKey: 'tableMaps.wizard.rectangleTable',
    },
];

// Preview card rendered pixel size — figure is scaled via viewBox
const CARD_SVG_W = 100;
const CARD_SVG_H = 100;

const PALETTE_VIEWBOX_S: number = (() => {
    let maxHalf = 0;
    for (const config of TABLE_TYPES) {
        const figW = config.w * ROOM.width;
        const figH = config.h * ROOM.height;
        const base = Math.min(figW, figH);
        const chairSize = base * 0.15;
        const chairGap = base * 0.1;
        const xHalf = figW / 2 + chairGap + 2 * chairSize;
        const yHalf = figH / 2 + chairGap + 2 * chairSize;
        maxHalf = Math.max(maxHalf, xHalf, yHalf);
    }
    return maxHalf * 1.25;
})();
const PALETTE_CARD_VIEWBOX = `${-PALETTE_VIEWBOX_S} ${-PALETTE_VIEWBOX_S} ${
    2 * PALETTE_VIEWBOX_S
} ${2 * PALETTE_VIEWBOX_S}`;

const useStyles = makeStyles({
    root: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    body: {
        display: 'flex',
        gap: 0,
        alignItems: 'flex-start',
    },
    leftPanel: {
        width: 272,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        paddingRight: 24,
        boxSizing: 'border-box' as const,
    },
    panelHeading: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        marginBottom: 4,
    },
    typeCard: {
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'grab',
        backgroundColor: 'white',
        userSelect: 'none' as const,
        transition: 'box-shadow 0.15s',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
        '&:active': {
            cursor: 'grabbing',
        },
    },
    typeCardLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    typeCardSvg: {
        flexShrink: 0,
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    rightSpacer: {
        width: 272,
        flexShrink: 0,
    },
    infoBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 6,
        backgroundColor: MAGENTA_LIGHT,
        border: `1px solid ${MAGENTA_BORDER}`,
    },
    infoBannerIcon: {
        color: MAGENTA,
        fontSize: 20,
        flexShrink: 0,
    },
    infoBannerText: {
        fontSize: 13,
        color: MAGENTA,
        fontWeight: 500,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    deleteButton: {
        color: ERROR_COLOR,
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: 13,
        padding: '4px 8px',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
    },
    deleteIcon: {
        fontSize: 18,
        color: ERROR_COLOR,
    },
    zoomControls: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    zoomLabel: {
        minWidth: 48,
        textAlign: 'center' as const,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        lineHeight: '32px',
        userSelect: 'none' as const,
    },
    canvasWrapper: {
        width: '100%',
        height: 440,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
        position: 'relative' as const,
    },
});

function clientToSVGPoint(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

function clampTableCenter(
    nx: number,
    ny: number,
    nw: number,
    nh: number
): { x: number; y: number } {
    const halfW = nw / 2;
    const halfH = nh / 2;
    return {
        x: Math.min(1 - halfW, Math.max(halfW, nx)),
        y: Math.min(1 - halfH, Math.max(halfH, ny)),
    };
}

/** Convert SVG point to normalized room coords (0..1) */
function svgToNormalized(svgX: number, svgY: number): { nx: number; ny: number } {
    return {
        nx: (svgX - ROOM.x) / ROOM.width,
        ny: (svgY - ROOM.y) / ROOM.height,
    };
}

interface PlacedTableProps {
    table: EditorTable;
    svgRef: React.RefObject<SVGSVGElement>;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onDragEnd: (id: string, x: number, y: number) => void;
}

const PlacedTable = React.memo(function PlacedTable({
    table,
    svgRef,
    isSelected,
    onSelect,
    onDragEnd,
}: PlacedTableProps) {
    const gRef = useRef<SVGGElement>(null);

    const dragActive = useRef(false);
    const didDrag = useRef(false);
    const dragStart = useRef({
        startNx: 0,
        startNy: 0,
        startPointerSvgX: 0,
        startPointerSvgY: 0,
    });

    const figW = table.width * ROOM.width;
    const figH = table.height * ROOM.height;
    const cx = ROOM.x + table.x * ROOM.width;
    const cy = ROOM.y + table.y * ROOM.height;

    const handlePointerDown = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            e.stopPropagation();
            if (!svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.setPointerCapture(e.pointerId);
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            dragActive.current = true;
            didDrag.current = false;
            dragStart.current = {
                startNx: table.x,
                startNy: table.y,
                startPointerSvgX: pt.x,
                startPointerSvgY: pt.y,
            };
            gEl.style.cursor = 'grabbing';
        },
        [table.x, table.y, svgRef]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
            const dsvgX = pt.x - dragStart.current.startPointerSvgX;
            const dsvgY = pt.y - dragStart.current.startPointerSvgY;

            if (!didDrag.current && Math.hypot(dsvgX, dsvgY) > DRAG_THRESHOLD) {
                didDrag.current = true;
            }

            if (didDrag.current) {
                const dnx = dsvgX / ROOM.width;
                const dny = dsvgY / ROOM.height;
                const clamped = clampTableCenter(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    table.width,
                    table.height
                );
                if (gRef.current) {
                    const newCx = ROOM.x + clamped.x * ROOM.width;
                    const newCy = ROOM.y + clamped.y * ROOM.height;
                    gRef.current.setAttribute('transform', `translate(${newCx}, ${newCy})`);
                }
            }
        },
        [svgRef, table.width, table.height]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent<SVGGElement>) => {
            if (!dragActive.current || !svgRef.current) return;
            const gEl = e.currentTarget as SVGGElement;
            gEl.style.cursor = 'grab';
            dragActive.current = false;

            if (!didDrag.current) {
                onSelect(table.id);
            } else {
                const pt = clientToSVGPoint(svgRef.current, e.clientX, e.clientY);
                const dsvgX = pt.x - dragStart.current.startPointerSvgX;
                const dsvgY = pt.y - dragStart.current.startPointerSvgY;
                const dnx = dsvgX / ROOM.width;
                const dny = dsvgY / ROOM.height;
                const clamped = clampTableCenter(
                    dragStart.current.startNx + dnx,
                    dragStart.current.startNy + dny,
                    table.width,
                    table.height
                );
                onDragEnd(table.id, clamped.x, clamped.y);
            }
        },
        [svgRef, table.id, table.width, table.height, onSelect, onDragEnd]
    );

    return (
        <g
            ref={gRef}
            transform={`translate(${cx}, ${cy})`}
            style={{ cursor: 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <TableFigure
                width={figW}
                height={figH}
                seats={table.seats}
                shape={table.shape}
                label={table.label}
                isActive={table.isActive}
                rotation={table.rotation}
            />
            {isSelected && <SelectionHighlight figW={figW} figH={figH} />}
        </g>
    );
});

export default function TablePlacementStep() {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();

    const [zoom, setZoom] = useState(1.0);
    const svgRef = useRef<SVGSVGElement>(null);
    const [selectedTableId, setSelectedTableId] = useState<string | null>(null);

    const [placingConfig, setPlacingConfig] = useState<TableTypeConfig | null>(null);
    const placingConfigRef = useRef<TableTypeConfig | null>(null);

    const ghostGRef = useRef<SVGGElement>(null);
    const pendingPosRef = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
    const pendingLabelRef = useRef<string>('');

    const handleTableDragEnd = useCallback(
        (id: string, x: number, y: number) => {
            dispatch({ type: 'UPDATE_TABLE_POSITION', payload: { id, x, y } });
        },
        [dispatch]
    );

    const handleSelect = useCallback((id: string) => {
        setSelectedTableId((prev) => (prev === id ? null : id));
    }, []);

    const handleDelete = useCallback(
        (id: string) => {
            dispatch({ type: 'REMOVE_TABLE', payload: { id } });
            setSelectedTableId(null);
        },
        [dispatch]
    );

    function handleZoomIn() {
        setZoom((prev) => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
    }

    function handleZoomOut() {
        setZoom((prev) => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
    }

    function getSvgPoint(clientX: number, clientY: number): { x: number; y: number } {
        if (!svgRef.current) return { x: 0, y: 0 };
        return clientToSVGPoint(svgRef.current, clientX, clientY);
    }

    function handlePalettePointerDown(
        config: TableTypeConfig,
        e: React.PointerEvent<HTMLDivElement>
    ) {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        pendingLabelRef.current = `T${draft.tables.length + 1}`;
        placingConfigRef.current = config;
        if (ghostGRef.current) {
            const svgPt = getSvgPoint(e.clientX, e.clientY);
            const { nx, ny } = svgToNormalized(svgPt.x, svgPt.y);
            const clamped = clampTableCenter(nx, ny, config.w, config.h);
            pendingPosRef.current = clamped;
            const gcx = ROOM.x + clamped.x * ROOM.width;
            const gcy = ROOM.y + clamped.y * ROOM.height;
            ghostGRef.current.setAttribute('transform', `translate(${gcx}, ${gcy})`);
        }
        setPlacingConfig(config);
    }

    function handlePalettePointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const config = placingConfigRef.current;
        if (!config || !ghostGRef.current) return;
        const svgPt = getSvgPoint(e.clientX, e.clientY);
        const { nx, ny } = svgToNormalized(svgPt.x, svgPt.y);
        const clamped = clampTableCenter(nx, ny, config.w, config.h);
        pendingPosRef.current = clamped;
        const cx = ROOM.x + clamped.x * ROOM.width;
        const cy = ROOM.y + clamped.y * ROOM.height;
        ghostGRef.current.setAttribute('transform', `translate(${cx}, ${cy})`);
    }

    function handlePalettePointerUp(e: React.PointerEvent<HTMLDivElement>) {
        const config = placingConfigRef.current;
        placingConfigRef.current = null;
        if (ghostGRef.current) {
            ghostGRef.current.setAttribute('transform', 'translate(-9999, -9999)');
        }
        setPlacingConfig(null);

        if (!config) return;

        const svgPt = getSvgPoint(e.clientX, e.clientY);
        const overRoom =
            svgPt.x >= ROOM.x &&
            svgPt.x <= ROOM.x + ROOM.width &&
            svgPt.y >= ROOM.y &&
            svgPt.y <= ROOM.y + ROOM.height;

        if (overRoom) {
            const { nx, ny } = svgToNormalized(svgPt.x, svgPt.y);
            const clamped = clampTableCenter(nx, ny, config.w, config.h);
            const newTable: Omit<EditorTable, 'qrCode'> = {
                id: `table-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                label: pendingLabelRef.current,
                shape: config.shape,
                seats: config.seats,
                x: clamped.x,
                y: clamped.y,
                width: config.w,
                height: config.h,
                rotation: 0,
                isActive: true,
            };
            dispatch({ type: 'ADD_TABLE', payload: newTable });
        }
    }

    const zoomPercent = Math.round(zoom * 100);

    const placedTablesLayer = useMemo(
        () =>
            draft.tables.map((table) => (
                <PlacedTable
                    key={table.id}
                    table={table}
                    svgRef={svgRef}
                    isSelected={table.id === selectedTableId}
                    onSelect={handleSelect}
                    onDragEnd={handleTableDragEnd}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draft.tables, selectedTableId, handleSelect, handleTableDragEnd]
    );

    return (
        <div className={classes.root}>
            <SectionTitle>{i18n.t('tableMaps.wizard.tablePlacement')}</SectionTitle>

            <div className={classes.infoBanner}>
                <InfoOutlinedIcon className={classes.infoBannerIcon} />
                <Typography className={classes.infoBannerText}>
                    {i18n.t('tableMaps.wizard.dragTablesHint')}
                </Typography>
            </div>

            <div className={classes.body}>
                <div className={classes.leftPanel}>
                    <Typography className={classes.panelHeading}>
                        {i18n.t('tableMaps.wizard.tableTypes')}
                    </Typography>
                    {TABLE_TYPES.map((config) => {
                        const figW = config.w * ROOM.width;
                        const figH = config.h * ROOM.height;

                        return (
                            <div
                                key={config.shape}
                                className={classes.typeCard}
                                onPointerDown={(e) => handlePalettePointerDown(config, e)}
                                onPointerMove={handlePalettePointerMove}
                                onPointerUp={handlePalettePointerUp}
                            >
                                <svg
                                    width={CARD_SVG_W}
                                    height={CARD_SVG_H}
                                    viewBox={PALETTE_CARD_VIEWBOX}
                                    className={classes.typeCardSvg}
                                >
                                    <TableFigure
                                        width={figW}
                                        height={figH}
                                        seats={config.seats}
                                        shape={config.shape}
                                    />
                                </svg>
                                <Typography className={classes.typeCardLabel}>
                                    {i18n.t(config.labelKey)}
                                </Typography>
                            </div>
                        );
                    })}
                </div>

                <div className={classes.canvasColumn}>
                    <div className={classes.toolbar}>
                        <div className={classes.toolbarLeft}>
                            {selectedTableId && (
                                <Button
                                    className={classes.deleteButton}
                                    disableRipple
                                    onClick={() => handleDelete(selectedTableId)}
                                    startIcon={<DeleteOutlineIcon className={classes.deleteIcon} />}
                                >
                                    {i18n.t('tableMaps.wizard.delete')}
                                </Button>
                            )}
                        </div>
                        <div className={classes.zoomControls}>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomOut}
                                disabled={zoom <= ZOOM_MIN}
                                type="button"
                                aria-label="Zoom out"
                            >
                                −
                            </button>
                            <span className={classes.zoomLabel}>{zoomPercent}%</span>
                            <button
                                className={classes.zoomButton}
                                onClick={handleZoomIn}
                                disabled={zoom >= ZOOM_MAX}
                                type="button"
                                aria-label="Zoom in"
                            >
                                +
                            </button>
                        </div>
                    </div>

                    <div
                        className={classes.canvasWrapper}
                        style={{
                            cursor: placingConfig ? 'crosshair' : 'default',
                        }}
                    >
                        <MapEditorCanvas
                            gridEnabled={draft.gridEnabled}
                            zoom={zoom}
                            entrance={draft.entrance}
                            isMarkingEntrance={false}
                            onSelectWall={() => undefined}
                            onMoveEntrance={() => undefined}
                            svgRef={svgRef}
                        >
                            <rect
                                x={ROOM.x}
                                y={ROOM.y}
                                width={ROOM.width}
                                height={ROOM.height}
                                fill="transparent"
                                onPointerDown={() => setSelectedTableId(null)}
                            />
                            {placedTablesLayer}
                            <g
                                ref={ghostGRef}
                                transform="translate(-9999, -9999)"
                                opacity={0.7}
                                pointerEvents="none"
                            >
                                {placingConfig && (
                                    <TableFigure
                                        width={placingConfig.w * ROOM.width}
                                        height={placingConfig.h * ROOM.height}
                                        seats={placingConfig.seats}
                                        shape={placingConfig.shape}
                                        label={pendingLabelRef.current}
                                    />
                                )}
                            </g>
                        </MapEditorCanvas>
                    </div>
                </div>

                <div className={classes.rightSpacer} />
            </div>
        </div>
    );
}
